/**
 * O conteúdo de fábrica de "Combate" — o motor da luta, com as três fontes cruzadas.
 *
 * <p><b>De onde cada linha veio:</b> as <b>Mecânicas Místikas</b> (o design original —
 * fórmulas de ataque, reações, PA, distância 0–4, fuga em 1d6), o <b>GDD v3.3</b> (o
 * cânone — as quatro cenas, a estrutura dos pontos de ação "resolvida em v3.1", morte
 * definitiva na temporada) e o <b>épico-07</b> (a fotografia do motor de código:
 * multi-participante até 4×4, ordem de resolução do crítico, custos em config).
 * O rascunho foi revisado pelo Theo em 22/08/2026 antes de virar esta página.
 *
 * <p><b>O que esta página NÃO cobre, de propósito:</b> o catálogo de ações, os custos
 * por ação, as ações iniciais e o aprendizado — isso é a página "Ações de combate".
 * As escadas das condições moram em "Condições do Personagem"; aqui fica só o que o
 * motor faz com elas (tick, aplicação por crítico, duração). A tela antiga de rascunho
 * de Combate (etapas/ações/distâncias) foi aposentada pelo Theo junto com esta página
 * — o documento dela segue no banco, sob a chave `combate`, sem tela.
 *
 * <p>O que o time gravar por cima vence inteiro — este arquivo é o ponto de partida.
 */
import { iconeDeAcao, iconeDeCondicao, iconeDePericia, iconeDerivado, iconeDoMenu } from './iconesDeConteudo';
import type { GrupoDeQuadros, SecaoDeQuadros } from './PaginaDeQuadros';
import { col, montarTabela } from './tabelasEditaveis';
import type { Tabela } from './tabelasEditaveis';

// --------------------------------------------------------- a luta como cena

const COMBATE_EM_CENAS = montarTabela(
  [col('cena', 'Cena'), col('faz', 'O que faz', true)],
  [
    [
      'Encontro / Embate',
      'A criatura ou o NPC aparece; dá para atacar, dialogar, ameaçar, negociar, dissimular, ignorar ou recuar. É aqui que se decide se haverá combate.',
    ],
    [
      'Combate',
      'O layout muda: a mochila e os botões gerais somem, e só restam as ações de combate disponíveis naquele instante. Cada oponente age na sua vez e reage na vez do outro.',
    ],
    ['Fuga', 'Um lado tenta sair, o outro decide se persegue.'],
    [
      'Resolução',
      'Vitória e saque, captura, rendição, fuga bem-sucedida ou morte — e devolve o jogador à cena em que ele estava antes do encontro.',
    ],
  ]
);

const SAIDAS = montarTabela(
  [col('saida', 'Saída'), col('rende', 'O que rende', true)],
  [
    [
      'Vitória',
      'A única que credita o XP do inimigo, as moedas e os drops (cada um rolado pela própria chance).',
    ],
    [
      'Fuga sua',
      'Sem XP do inimigo, moedas nem drops — mas o +1 XP por perícia usada vem: pratiquei Esquiva e Fuga, aprendi mesmo sem vencer.',
    ],
    ['Fuga do oponente', 'O combate acaba; o saque foi embora com ele.'],
    [
      'Derrota',
      'Perde 10% das moedas (combat.defeat_coin_loss_pct) e vai ao local seguro da região — nunca fica preso na cena. Oferta única e opcional de "Recuperar até amanhecer": avança o dia com a penalidade de dormir mal, avisando o custo antes de confirmar.',
    ],
    ['Morte', 'Vigor 0. Morte é definitiva na temporada: encerra o personagem.'],
  ]
);

// ------------------------------------------------------------------ a rodada

const RODADA = montarTabela(
  [col('momento', 'Momento'), col('acontece', 'O que acontece', true)],
  [
    [
      'Início do turno',
      'Recupera pontos de ação (somando ao saldo, até o teto) e as condições dão o tick: dano e cura por stack (Sangrando, Envenenado, Regenerando).',
    ],
    ['Durante', 'Gasta pontos em ataques, movimento, manobras e itens do cinto — quantas ações o orçamento permitir.'],
    ['Fim do turno', 'As durações de condição decrementam; a que zera, sai.'],
    [
      'Vez do oponente',
      'Só se reage — esquivar, bloquear, oportunidade — com o ponto que sobrou. Reagir hoje é não atacar amanhã.',
    ],
  ]
);

const INICIATIVA = montarTabela(
  [col('regra', 'Regra'), col('valor', 'O que vale', true)],
  [
    ['Fórmula (épico-07)', '1d6 + Agilidade×2 + Percepção − penalidade de carga + bônus de equipamento.'],
    ['Atributos (GDD)', 'Vem de Agilidade e Percepção — a fórmula não consta no GDD.'],
    ['Quando rola', 'No início do combate; recalculada quando uma invocação insere participante no meio da luta.'],
    ['Ordem', 'O maior resultado age primeiro; a ordem é exibida ao jogador.'],
    ['Empate', 'Não consta em nenhuma fonte — a decidir.'],
  ]
);

const PONTOS_DE_ACAO = montarTabela(
  [
    col('parametro', 'Parâmetro'),
    col('design', 'Valendo (design/GDD)'),
    col('codigo', 'Código hoje'),
    col('config', 'Config'),
  ],
  [
    ['Pontos iniciais (1ª rodada)', '5', '5', 'combat.base_ap'],
    ['Recuperação por turno', '+4', '+3', 'combat.ap_recovery'],
    ['Teto', '7', '5', 'combat.ap_cap'],
  ]
);

const DISTANCIA = montarTabela(
  [col('valor', 'Valor'), col('nome', 'Nome (épico-07)'), col('habilita', 'O que habilita', true)],
  [
    ['0', 'Engajado (agarrado)', 'Golpear.'],
    ['1', 'Corpo a Corpo', 'Golpear e Perfurar; fugir daqui abre ataque de oportunidade.'],
    ['2', 'Curto', 'Arremessar e Disparar.'],
    ['3', 'Longo', 'Disparar.'],
    ['4', 'Distante', 'Disparar.'],
  ]
);

// ----------------------------------------------------------- resolver o golpe

const TIPOS_DE_ATAQUE = montarTabela(
  [
    col('tipo', 'Tipo'),
    col('distancia', 'Distância'),
    col('acerto', 'Acerto (1d12 +)', true),
    col('dano', 'Dano', true),
    col('nota', 'Nota', true),
  ],
  [
    [
      'Perfurar',
      '1',
      'DES×2 + FOR + equipamento + perícia',
      'dano de perfurar + FOR + DES',
      'Preciso e rápido; contra armadura pesada depende de crítico.',
    ],
    [
      'Golpear',
      '0–1',
      'DES + FOR + equipamento + perícia',
      'dano de golpe + FOR×2',
      'O peso da arma e do corpo; atravessa redução com facilidade.',
    ],
    [
      'Disparar',
      '2–4',
      'DES×2 + PER + equipamento + perícia',
      'dano de disparar + DES',
      'Consome 1 munição por disparo; sem munição, some da lista.',
    ],
    [
      'Arremessar',
      '2',
      'DES + FOR + equipamento + perícia',
      'dano de arremesso + FOR',
      'A arma sai da mão. A prosa da fonte cita DES no dano; a fórmula não (inconsistência #16).',
    ],
  ]
);

const REACOES = montarTabela(
  [
    col('defesa', 'Defesa'),
    col('custo', 'Custo'),
    col('formula', 'Fórmula', true),
    col('efeito', 'Efeito', true),
  ],
  [
    [
      'Esquivar',
      '1 PA',
      '1d12 + AGI×2 + PER + perícia Esquiva + escudo − Armadura',
      'Superou o ataque: anula dano e efeito — tudo ou nada.',
    ],
    [
      'Bloquear',
      '1 PA',
      '1d12 + AGI + PER + perícia Bloquear + escudo/arma',
      'Superou por X: reduz o dano em X+1 — parcial, escalonado.',
    ],
    [
      'Esquiva base',
      '0',
      'AGI×2 + PER + perícia Esquiva + escudo − Armadura (sem dado)',
      'O piso de quem não reage: decide erro, acerto e crítico. Sempre pior que reagir de verdade.',
    ],
    [
      'Ataque de oportunidade',
      'a definir',
      '—',
      'Contra quem foge a distância 1, com ponto sobrando. Resolvido antes de a fuga resolver.',
    ],
  ]
);

const DANO = montarTabela(
  [col('regra', 'Regra'), col('valor', 'O que vale', true)],
  [
    ['Fórmula', 'Dano final = dano do ataque − (redução da defesa − penetração da arma).'],
    ['Piso', 'Mínimo 1 de dano quando o ataque acerta.'],
    [
      'Vigor máximo',
      'VIT×5 + FOR×1 + Nível + equipamentos + bônus (design). O código hoje usa 50 + VIT×10 — ver Conflitos.',
    ],
    ['Exemplo da fonte', 'Dano 6, armadura de couro reduz 2, o arco perfura 1 → 5 no Vigor.'],
  ]
);

const CRITICOS = montarTabela(
  [col('parametro', 'Parâmetro'), col('valor', 'O que vale', true), col('config', 'Config')],
  [
    ['Margem de crítico', '10 ou mais sobre a defesa usada (esquiva, bloqueio ou esquiva base)', 'combat.crit_margin'],
    ['Crítico no dado', '12 — e o +5 é registro de log, não soma ao dano', 'combat.crit_bonus'],
    ['Falha crítica', '1 — o ataque erra automaticamente (−5 no log)', 'combat.crit_fail_penalty'],
    ['Efeito v1', 'O dado de dano da arma ×2 — o dado de sorte não entra no dano', '—'],
    ['Taxa esperada', '~8% de crítico e ~8% de falha (1 em 12)', '—'],
  ]
);

// -------------------------------------------------------------- sair da luta

const FUGA = montarTabela(
  [col('regra', 'Regra'), col('valor', 'O que vale', true)],
  [
    ['Teste (ambos)', '1d6 + Agilidade×2 + perícia (config combat.flee_die).'],
    ['Fugitivo vence', 'Escapa; o combate acaba.'],
    ['Empate', 'Rerola o teste.'],
    ['Fugitivo perde', 'O combate continua, na vez do perseguidor.'],
    ['A distância 1', 'Fugir abre ataque de oportunidade, resolvido antes da fuga.'],
    ['Alvo de balanceamento', '5–20% das lutas terminando em fuga; acima de 50%, os inimigos estão fortes demais.'],
  ]
);

const CONDICOES_EM_COMBATE = montarTabela(
  [col('gancho', 'Gancho do motor'), col('regra', 'Regra', true)],
  [
    ['Tick', 'Dano e cura por stack no início do turno do afetado (Sangrando, Envenenado, Regenerando).'],
    [
      'Aplicação por crítico',
      'Pelo tipo de dano do golpe — enum fechado cortante | contundente | perfurante; cortante → Sangrando, contundente → Atordoado. O tipo mora na ação, não na arma (recomendação §2.7).',
    ],
    ['Duração', 'Decrementa no fim do turno; a que zera, sai.'],
    [
      'Contramedidas em combate',
      'Só Levantar (2 PA) e Soltar-se (teste oposto). Medicar é pós-combate — a mochila está fechada: Sangrando não sai durante a luta ("vença ou fuja").',
    ],
  ]
);

// ------------------------------------------------------------------ conflitos

const CONFLITOS = montarTabela(
  [
    col('assunto', 'Assunto'),
    col('fontes', 'O que as fontes dizem', true),
    col('codigo', 'O que o código faz', true),
    col('decisao', 'Decisão do Theo', true),
  ],
  [
    ['Dado do combate', '1d12 nas Mecânicas e no GDD; crítico no 12, falha no 1.', 'Roda d10 (DiceRoller e FormulaEvaluator).', ''],
    [
      'PA por turno e teto',
      'A regra diz +4/teto 7; a simulação oficial roda três vezes com +3. O GDD arbitrou na v3.1: vale 4/7. O épico recomenda prototipar +3 (1 ataque + 1 reação por rodada) e só subir se a planilha mostrar turnos pobres.',
      '+3, teto 5 (configs combat.ap_recovery / ap_cap).',
      '',
    ],
    [
      'Dado da fuga',
      '1d6 de propósito — mais arriscada, mais dramática (épico). O GDD cobra: justificar a exceção por escrito ou padronizar (inconsistência #4).',
      'Roda no dado de combate (d10).',
      '',
    ],
    ['Vigor máximo', 'VIT×5 + FOR + Nível + equipamentos + bônus.', '50 + VIT×10.', ''],
    [
      'Fórmulas dos 4 ataques',
      'Multiplicadores por tipo (Perfurar DES×2+FOR; Golpear dano FOR×2; Disparar DES×2+PER…).',
      'Fórmulas legadas sem os multiplicadores — reseed é a story I7.1b.',
      '',
    ],
    [
      'Dois críticos (#5 do GDD)',
      'O crítico do teste (±5 no resultado) e o crítico de ataque (multiplica dano) têm o mesmo nome e são sistemas diferentes — renomear um ou unificar. O épico crava a ordem de resolução (1 erra; margem sem o +5; dobra o dado de dano).',
      'Efeito v2 por tipo já implementado: cortante ignora 50% da armadura, contundente atordoa (−1 PA), perfurante +50% de dano.',
      '',
    ],
    [
      'Condição por crítico',
      'Sangrando (cortante) e Atordoado (contundente) por tag — recomendação: tipo de dano declarado na ação, eixo ortogonal ao efeito de crítico.',
      'Efeitos v1/v2 testados; reinterpretar conflita com eles.',
      '',
    ],
    ['Empate de iniciativa', 'Não consta em nenhuma fonte.', 'Não consta.', ''],
    [
      'Recuperação pós-derrota',
      'Bônus de turnos trivializa a punição; recomendação: dormir forçado de baixa qualidade — acorda no local seguro no turno 1, com a penalidade de descanso ruim, sem bônus.',
      '—',
      '',
    ],
    [
      'Agarrado e Impedido',
      'Agarrado como condição que força distância 0 e trava mover/fugir, só liberando Soltar-se. Impedido: trava movimento e permite atacar (proposta) — ou fora do Beta.',
      '—',
      '',
    ],
    [
      '"Vigor" como perícia',
      'A fonte usa a mesma palavra para a barra de vida e para uma perícia treinável — recomendação: renomear a perícia (ex.: "Resistência").',
      '—',
      '',
    ],
    [
      'Embate no Beta',
      'Sim — é dependência dos Épicos 1 e 8, não gap deste. Rendição e Captura ficam para depois do Beta (gancho de quest).',
      'A cena de Embate não existe.',
      '',
    ],
    [
      'Ownership do combate multi',
      'Endpoints de leitura e avanço de turno não validam o dono — risco de segurança ativo no PvE (griefing por UUID). Não-negociável no Beta (épico, S7.2.3).',
      'GET multi e run-npc-turns sem validação de dono.',
      '',
    ],
  ]
);

// ------------------------------------------------------------ a aba Season 2+

const MAGIA_EM_COMBATE = montarTabela(
  [col('regra', 'Regra'), col('valor', 'O que vale', true)],
  [
    [
      'O que já existe',
      'As perícias Combater com Magia e Escudo Arcano, o custo de energia, pergaminhos e grimórios de magia, e habilidades avulsas listadas.',
    ],
    [
      'O que falta',
      'O sistema inteiro: como se aprende a primeira magia, o que distingue uma escola da outra, como progride. É o maior vazio da Parte VI do GDD.',
    ],
    ['Exemplo de custo', 'Bola de fogo: 5 PA + Energia (SP) — o motor já suporta custo de SP.'],
    ['Gatilho', 'Precisa de decisão antes da Season 1 se magia for jogável.'],
  ]
);

const CRITICO_V2 = montarTabela(
  [col('regra', 'Regra'), col('valor', 'O que vale', true)],
  [
    ['Hoje (v1)', 'O crítico multiplica o dado de dano da arma ×2.'],
    ['A versão dois', 'Cada tipo de arma tem um efeito crítico próprio.'],
    [
      'No código, já',
      'Cortante ignora 50% da armadura; contundente aplica Atordoado (−1 PA); perfurante +50% de dano.',
    ],
    [
      'Depende de',
      'Fechar as condições de combate — a maioria dos efeitos críticos seria aplicar uma delas (backlog do GDD).',
    ],
  ]
);

const PVP = montarTabela(
  [col('regra', 'Regra'), col('valor', 'O que vale', true)],
  [
    ['O caminho Duelista', '"Vence outros jogadores em confronto direto" — anunciado publicamente.'],
    ['Mecânica que o sustenta', 'Nenhuma. PvP não está definido — o GDD marca como divergência crítica.'],
    [
      'No Beta e na Season 1',
      'Nenhuma infra de PvP entra (websocket, lobby, sync). A trava de ownership do combate é entregue pelo valor anti-trapaça PvE.',
    ],
  ]
);

// ---------------------------------------------------------- prosa e avisos

const PROSA: Record<string, Pick<Tabela, 'texto' | 'alerta'>> = {
  'combate-em-cenas': {
    texto:
      'O combate é uma cena com regras próprias, não um módulo separado. Ele não consome turno do dia — quem pagou o turno foi a ação que levou até ali; o que ele consome é energia, por ação executada. O layout muda: a mochila e os botões gerais somem, e em combate só existe o que está equipado e no cinto. Lutar deve ser uma entre várias respostas ao encontro: a cena de Embate existe para a conta ser feita antes do primeiro golpe. O motor generaliza de 1×1 até 4×4 (lados limitados a 4), com aliados, companions e invocação no meio da luta; com um inimigo só, a tela colapsa para o duelo clássico.',
    alerta:
      'O Embate não é entregue pelo épico-07 — é dependência dos Épicos 1 e 8. Rendição e Captura estão explicitamente fora do Beta (gancho de quest).',
  },
  saidas: {
    texto:
      'Cinco saídas, e só a vitória paga o saque. O XP de perícia (+1 por perícia usada, com teto de 1 por perícia por combate) é creditado em qualquer saída — vitória, fuga e derrota. Fuga de encontro gerado por descanso não concede experiência (anti-exploit do GDD).',
    alerta:
      'Derrota não é morte: o épico a modela como perda de 10% das moedas + respawn no local seguro ("não é game over"); o GDD diz que Vigor 0 encerra o personagem na temporada. O que separa "ser derrotado" de "morrer" precisa de uma linha do Theo. Sangrando e Envenenado persistem após a luta e só saem em Descansar → Medicar.',
  },
  'rodada-e-turnos': {
    texto:
      'O combate corre em turnos: cada combatente tem a sua vez, faz quantas ações o orçamento de pontos permitir e passa adiante; ao chegar na última iniciativa, a rodada se encerra e começa outra. No multi, companions e inimigos agem sozinhos pela IA até a vez voltar ao jogador, e o log mostra o que cada um fez. Duração-alvo de uma luta: 3 a 8 turnos — menos de 2 é trivial, mais de 12 arrasta.',
  },
  iniciativa: {
    texto:
      'No início do combate, cada combatente rola iniciativa; o maior resultado age primeiro. A carga morde: cada faixa acima de Super Leve reduz a Agilidade em um degrau, o que ataca iniciativa, esquiva e fuga. Armas podem ter penalidade de iniciativa própria.',
    alerta:
      'As Mecânicas Místikas não fecham a fórmula (só os componentes: Agilidade, Percepção, penalidade da arma); quem a crava é o épico-07 — e note que iniciativa e fuga são os únicos testes fora do 1d12.',
  },
  'pontos-de-acao': {
    texto:
      'O orçamento da rodada — a mesma tensão que governa o dia, dentro do combate. Toda ação e toda reação custam pontos; o que não se gasta sobra, financia reações no turno do oponente e soma à recuperação seguinte, até o teto que força o gasto. Guardar ponto para esquivar em vez de gastar tudo atacando é o coração da tática. O que é canônico é a estrutura; ganho e teto são balanceamento. Atordoado tira 1 da recuperação (nunca abaixo de 0).',
    alerta:
      'A própria fonte se contradiz: a regra diz +4/teto 7 e a simulação oficial roda três vezes com +3. O GDD arbitrou na v3.1 (vale 4/7, corrigir a simulação); o épico, depois, recomenda prototipar +3 e só subir para +4 se a planilha mostrar turnos pobres. É config — troca sem deploy. Decisão do Theo pendente, ver Conflitos.',
  },
  distancia: {
    texto:
      'Entre os combatentes existe uma régua de cinco degraus, do corpo colado ao tiro longo; a cena de encontro define o degrau inicial. Cada arma e cada ação exigem degraus específicos, e mover sobe ou desce a régua ao custo de pontos — Avançar e Recuar movem 1 por 1 PA; os rápidos movem 2 (custo a definir). Aproximar-se de quem atira é o problema do guerreiro; manter o espaço, o do arqueiro — quem vence controlou o espaço, não empunhava a arma maior. Empurrar move o alvo 1 à força (anti-kite); Agarrado força distância 0 e trava movimento e fuga. No multi, a régua vira geometria por posição: adjacente é golpeável.',
    alerta: 'Os nomes dos degraus são do épico-07 — o GDD só nomeia as pontas ("do corpo colado ao tiro longo").',
  },
  'tipos-de-ataque': {
    texto:
      'A escolha de atributos aparece no combate como estilo, não só como número maior. A mesma arma costuma servir a mais de um tipo, com valores diferentes — é a ficha da arma que define quais estão disponíveis. Perfurar e Disparar fazem pouco dano de propósito: contra armadura pesada, o crítico é o caminho — o que torna a regra de crítico questão de viabilidade de build. A fórmula-base de todo teste: 1d12 + equipamento + perícia + atributo primário×2 + secundários + bônus − penalidades.',
    alerta:
      'O código roda d10 e tem fórmulas legadas sem os multiplicadores — adotar o 1d12 e ressementar as quatro fórmulas são as stories I7.1a e I7.1b do épico. A distância do Arremessar (só 2?) vem do épico; conferir com a planilha de ações.',
  },
  reacoes: {
    texto:
      'Defender custa: as duas reações gastam ponto fora do próprio turno, e é isso que faz "não atacar" ser jogada legítima. Cada ação declara quais reações o defensor pode opor — um disparo pode ser esquivável e não bloqueável. Quem não tem ponto, não pode ou não quer reagir usa a esquiva base, um valor estático da ficha: garante que nenhum ataque acerte automático, mas é sempre pior que reagir de verdade.',
    alerta:
      'A fonte descreve a Esquiva mandando "fazer um teste de Bloqueio" — erro de redação com efeito mecânico, já registrado (inconsistência #10). E note a assimetria de propósito: a Armadura subtrai da esquiva e não do bloqueio — armadura pesada é tomar menos e ser atingido mais vezes.',
  },
  dano: {
    texto:
      'O que sobra sai do Vigor — com mínimo 1 quando o ataque acerta. Algumas armas têm Penetração, que devolve parte da redução da armadura ("Penetração" e "Perfuração" na fonte são a mesma propriedade).',
  },
  criticos: {
    texto:
      'Dois gatilhos: superar a defesa usada em 10 ou mais, ou tirar 12 no dado. Dado 1 é falha crítica: o ataque erra automaticamente. Ordem de resolução cravada pelo épico: rola; 1 erra; calcula a margem sem somar o +5 (para não realimentar); crítico dobra o dado de dano da arma. O v2 — efeito por tipo de arma — mora na aba Season 2+.',
    alerta:
      '"Dois críticos com o mesmo nome" é inconsistência Alta do GDD (#5): o crítico do teste (±5 no resultado) e o crítico de ataque (multiplica dano) são sistemas diferentes — renomear um ou unificar. O épico resolve a convivência na ordem acima; falta o carimbo do Theo.',
  },
  fuga: {
    texto:
      'A saída de emergência do sistema — se ela não funcionar, o jogo inteiro fica mais covarde do que deveria. Fugir encerra o turno; o oponente decide se persegue ou corta a perda (ir atrás custa pontos e expõe a outro encontro).',
    alerta:
      'A fuga é a única mecânica com dado diferente do resto (1d6) — o GDD exige justificar por escrito ou padronizar; o épico recomenda manter o 1d6 como escolha consciente (mais arriscada, mais dramática). Decisão do Theo pendente. O código hoje roda a fuga em d10.',
  },
  'condicoes-em-combate': {
    texto:
      'As escadas das condições — Atordoado, Caído, Agarrado, Sangrando e as demais — moram na página Condições do Personagem, com os cinco tiers de cada uma. O que é deste quadro é o que o motor faz com elas durante a luta.',
    alerta:
      'Nas Mecânicas as condições de combate estão nomeadas sem efeito ("Em desenvolvimento…"), e o GDD marca isso como a pendência CRÍTICA que bloqueia a implementação do combate. Preencher os tiers na página de Condições é destravar este motor.',
  },
  conflitos: {
    texto:
      'Onde as fontes dizem coisas diferentes sobre a luta. A coluna do Theo nasce vazia de propósito: um chute pré-preenchido se lê como decisão tomada.',
  },
};

const PROSA_S2: Record<string, Pick<Tabela, 'texto' | 'alerta'>> = {
  'magia-em-combate': {
    texto:
      'O maior vazio do combate: as peças existem — perícias, custo de energia, portadores —, o sistema não. Nada de conteúdo de magia vira seed antes de o sistema ser escrito.',
  },
  'critico-v2': {
    texto:
      'A versão dois do crítico: em vez de multiplicar o dano, cada tipo de arma aplica um efeito próprio — e a maioria desses efeitos é aplicar uma condição. Por isso ele espera as escadas das condições fecharem.',
  },
  pvp: {
    texto:
      'O duelo entre jogadores é caminho anunciado sem mecânica por trás — o GDD o marca como divergência crítica. Fica aqui como registro do que o combate multi um dia vai sustentar.',
  },
};

// ------------------------------------------------------------------ o mapa

const QUADROS: Record<string, Tabela> = {
  'combate-em-cenas': COMBATE_EM_CENAS,
  saidas: SAIDAS,
  'rodada-e-turnos': RODADA,
  iniciativa: INICIATIVA,
  'pontos-de-acao': PONTOS_DE_ACAO,
  distancia: DISTANCIA,
  'tipos-de-ataque': TIPOS_DE_ATAQUE,
  reacoes: REACOES,
  dano: DANO,
  criticos: CRITICOS,
  'condicoes-em-combate': CONDICOES_EM_COMBATE,
  fuga: FUGA,
  conflitos: CONFLITOS,
};

/** A aba Beta/Season 1 — a chave `combate-resumo` no servidor. */
export const TABELAS: Record<string, Tabela> = Object.fromEntries(
  Object.entries(QUADROS).map(([chave, quadro]) => [chave, { ...PROSA[chave], ...quadro }])
);

export const SECOES: Record<string, SecaoDeQuadros> = {
  'combate-em-cenas': {
    titulo: 'Combate narrativo em cenas',
    rotuloDoItem: 'cena',
    icone: iconeDoMenu('sol'),
    iconeDaLinha: (l) => iconeDeAcao(l.valores.cena ?? ''),
  },
  saidas: { titulo: 'As saídas do combate', rotuloDoItem: 'saída', icone: iconeDoMenu('fluxo') },
  'rodada-e-turnos': { titulo: 'Rodada e turnos', rotuloDoItem: 'momento', icone: iconeDoMenu('transcricao') },
  iniciativa: { titulo: 'Iniciativa', rotuloDoItem: 'regra', icone: iconeDerivado('iniciativa') },
  'pontos-de-acao': { titulo: 'Pontos de Ação', rotuloDoItem: 'parâmetro', icone: iconeDeCondicao('energia') },
  distancia: { titulo: 'Distância', rotuloDoItem: 'degrau', numerada: true, icone: iconeDoMenu('losango') },
  'tipos-de-ataque': {
    titulo: 'Os quatro tipos de ataque',
    rotuloDoItem: 'tipo',
    icone: iconeDerivado('acerto'),
    iconeDaLinha: (l) => iconeDeAcao(l.valores.tipo ?? ''),
  },
  reacoes: {
    titulo: 'As reações e a esquiva base',
    rotuloDoItem: 'defesa',
    icone: iconeDerivado('defesa'),
    iconeDaLinha: (l) => iconeDeAcao(l.valores.defesa ?? ''),
  },
  dano: { titulo: 'Dano, armadura e penetração', rotuloDoItem: 'regra', icone: iconeDerivado('dano') },
  criticos: { titulo: 'Os críticos', rotuloDoItem: 'parâmetro', icone: iconeDoMenu('dado') },
  'condicoes-em-combate': {
    titulo: 'Condições em combate',
    rotuloDoItem: 'gancho',
    icone: iconeDerivado('condicao'),
  },
  fuga: { titulo: 'Fuga e perseguição', rotuloDoItem: 'regra', icone: iconeDoMenu('mapa') },
  conflitos: { titulo: 'Conflitos e decisões', rotuloDoItem: 'conflito', destaque: true, icone: iconeDoMenu('alvo') },
};

/** Os macro-títulos, na ordem do rascunho que o Theo revisou (22/08/2026). */
export const GRUPOS: GrupoDeQuadros[] = [
  { id: 'cena', titulo: 'A luta como cena', quadros: ['combate-em-cenas', 'saidas'] },
  { id: 'rodada', titulo: 'A rodada', quadros: ['rodada-e-turnos', 'iniciativa', 'pontos-de-acao', 'distancia'] },
  {
    id: 'golpe',
    titulo: 'Resolver o golpe',
    quadros: ['tipos-de-ataque', 'reacoes', 'dano', 'criticos', 'condicoes-em-combate'],
  },
  { id: 'sair', titulo: 'Sair da luta', quadros: ['fuga'] },
  { id: 'disputa', titulo: 'Em disputa', quadros: ['conflitos'] },
];

const QUADROS_S2: Record<string, Tabela> = {
  'magia-em-combate': MAGIA_EM_COMBATE,
  'critico-v2': CRITICO_V2,
  pvp: PVP,
};

/** A aba Season 2+ — a chave `combate-season2`, gravada à parte. */
export const TABELAS_S2: Record<string, Tabela> = Object.fromEntries(
  Object.entries(QUADROS_S2).map(([chave, quadro]) => [chave, { ...PROSA_S2[chave], ...quadro }])
);

export const SECOES_S2: Record<string, SecaoDeQuadros> = {
  'magia-em-combate': {
    titulo: 'Magia em combate',
    rotuloDoItem: 'regra',
    icone: iconeDePericia('arcana'),
  },
  'critico-v2': { titulo: 'Crítico por tipo de arma (v2)', rotuloDoItem: 'regra', icone: iconeDerivado('dano') },
  pvp: { titulo: 'PvP — o Duelista', rotuloDoItem: 'regra', icone: iconeDoMenu('pessoas') },
};

export const GRUPOS_S2: GrupoDeQuadros[] = [
  { id: 'backlog', titulo: 'O que fica para depois', quadros: ['magia-em-combate', 'critico-v2', 'pvp'] },
];
